// src/reports/ClubMemberAnalyticsReport.jsx — Reporting → Store Reports → Club Member Analytics
// (Eric, 2026-10-16). Replaces the Grafana "Club Member Lightspeed Analytics Dashboard". Club members come
// from the Team Portal (synced nightly, club_member_analytics.py); a person counts under their MOST RECENT
// club; orders are Lightspeed sales matched on email; pickups are OpsHub's own pickup records.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    Card, BlockStack, InlineStack, InlineGrid, Text, Banner, Button, Tabs, TextField, Tooltip, Spinner, Box, Modal, Collapsible, List, Badge,
} from '@shopify/polaris';
import { useSearchParams } from 'react-router-dom';
import { useAuthFetch } from '../AuthComponent';
import { ReportDateRange, ReportPicker, isoDaysAgo, todayIso, downloadCsv, paneHeightToFitScroller } from '../Reporting';

const fmtN = n => (n === null || n === undefined) ? '—' : Number(n).toLocaleString();
const fmtUSD = n => (n === null || n === undefined) ? '—' : '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtP = n => (n === null || n === undefined) ? '—' : Number(n).toFixed(1) + '%';

// green (high) → red (low) tint for engagement / conversion
const heat = (v, hi) => {
    const t = Math.max(0, Math.min(1, (Number(v) || 0) / hi));
    return { background: `hsl(${Math.round(t * 120)}, 55%, 88%)`, borderRadius: 4, padding: '2px 6px', display: 'inline-block', minWidth: 52, textAlign: 'right' };
};

// Plain-language help for every column (tooltip on the header).
const COL_HELP = {
    club: 'The club. Each person is counted under ONE club: the one they joined most recently.',
    store: "The store these members use: each person goes to the store of their most recent pickup, otherwise their most recent store sale, otherwise the club's usual pickup store. A club can appear on several rows, one per store.",
    club_members: "The whole club's roster across every store, so you can see how big this store's share is.",
    clubs: 'How many clubs have this store as their home store.',
    members: 'People in the club (from the Team Portal) who use this store. Someone in two clubs is counted only in the newer one.',
    shopped: 'Members who bought something in a Soccer Post store (a Lightspeed sale) during the dates you picked.',
    engagement_pct: '% Engaged = Shopped ÷ Members. The share of the club that bought in a store.',
    orders: 'Store sales (transactions) rung up by those members during the dates.',
    revenue: 'Total dollars of those store sales.',
    rev_per_member: 'Revenue ÷ Members. What the average member spent, including members who spent nothing.',
    rev_per_shopper: 'Revenue ÷ Shopped. What the average buying member spent.',
    avg_order: 'Revenue ÷ Orders. The average size of one sale.',
    pickups: "Customers who picked up an order tagged with this club, at this store, during the dates. Counted by the order's club tag and its pickup store, so it matches the Orders list. Not limited to current members.",
    bought_at_pickup: 'Of those pickup customers, how many ALSO bought something in a store on the same day.',
    pickup_conversion_pct: 'Pickup Conv % = Bought @ Pickup ÷ Pickups. How often a pickup turns into an extra store purchase.',
    email: "The member's email address.",
    joined_at: 'When they joined their current club.',
    last_order_at: 'Their most recent store sale in the dates.',
    last_pickup_at: 'Their most recent pickup in the dates.',
    month: 'Calendar month (Eastern time).',
    shoppers: 'Different members who bought in a store that month.',
};

const CLUB_COLS = [
    ['club', 'Club', 's'], ['store', 'Store', 's'], ['members', 'Members at store', 'n'], ['club_members', 'Club total', 'n'], ['shopped', 'Shopped', 'n'],
    ['engagement_pct', '% Engaged', 'p'], ['orders', 'Orders', 'n'], ['revenue', 'Revenue', 'u'], ['rev_per_member', 'Rev / Member', 'u'],
    ['rev_per_shopper', 'Rev / Shopper', 'u'], ['avg_order', 'Avg Order', 'u'], ['pickups', 'Pickups', 'n'],
    ['bought_at_pickup', 'Bought @ Pickup', 'n'], ['pickup_conversion_pct', 'Pickup Conv %', 'p'],
];
const STORE_COLS = [
    ['store', 'Store', 's'], ['clubs', '# Clubs', 'n'], ['members', 'Members', 'n'], ['shopped', 'Shopped', 'n'],
    ['engagement_pct', '% Engaged', 'p'], ['orders', 'Orders', 'n'], ['revenue', 'Revenue', 'u'], ['rev_per_member', 'Rev / Member', 'u'],
    ['pickups', 'Pickups', 'n'], ['bought_at_pickup', 'Bought @ Pickup', 'n'], ['pickup_conversion_pct', 'Pickup Conv %', 'p'],
];
const MEMBER_COLS = [
    ['email', 'Email', 's'], ['store', 'Store', 's'], ['joined_at', 'Joined', 'd'], ['orders', 'Orders', 'n'], ['revenue', 'Revenue', 'u'],
    ['pickups', 'Pickups', 'n'], ['last_order_at', 'Last order', 'd'], ['last_pickup_at', 'Last pickup', 'd'],
];

const cell = (v, kind, key) => {
    if (kind === 'n') return fmtN(v);
    if (kind === 'u') return fmtUSD(v);
    if (kind === 'd') return v ? String(v).slice(0, 10) : '—';
    if (kind === 'p') return <span style={heat(v, key === 'engagement_pct' ? 30 : 25)}>{fmtP(v)}</span>;
    return v ?? '—';
};

function SortTable({ cols, rows, onRow, rowKey, maxHeight = 460, fit = false }) {
    const markMine = rows.some(r => r.mine === true) && rows.some(r => r.mine === false);   // only when the caller's scope is a subset
    const [sort, setSort] = useState({ key: null, dir: 'desc' });
    const wrapRef = useRef(null);
    const [paneH, setPaneH] = useState(null);
    useLayoutEffect(() => {
        if (!fit) return undefined;
        const measure = () => { const el = wrapRef.current; if (!el) return; const h = paneHeightToFitScroller(el); setPaneH(prev => (prev != null && Math.abs(prev - h) <= 1) ? prev : h); };
        measure();
        const t = setTimeout(measure, 300);   // the cards above settle (fonts, tiles) after first paint
        window.addEventListener('resize', measure);
        return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
    }, [fit, rows, paneH]);
    const sorted = useMemo(() => {
        if (!sort.key) return rows;
        const k = sort.key, d = sort.dir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const x = a[k], y = b[k];
            if (x === y) return 0;
            if (x === null || x === undefined || x === '') return 1;
            if (y === null || y === undefined || y === '') return -1;
            return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))) * d;
        });
    }, [rows, sort]);
    return (
        <div ref={wrapRef} style={{ overflow: 'auto', maxHeight: fit ? (paneH || undefined) : maxHeight }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--p-color-bg-surface, #fff)', zIndex: 1 }}>
                    <tr>{cols.map(([k, label, kind]) => (
                        <th key={k} onClick={() => setSort(s => ({ key: k, dir: s.key === k && s.dir === 'desc' ? 'asc' : 'desc' }))}
                            style={{ textAlign: kind === 's' ? 'left' : 'right', padding: '6px 8px', cursor: 'pointer', whiteSpace: 'nowrap', borderBottom: '1px solid var(--p-color-border, #ddd)' }}>
                            <Tooltip content={COL_HELP[k] || label}><span style={{ borderBottom: '1px dotted currentColor' }}>{label}</span></Tooltip>{sort.key === k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                        </th>))}</tr>
                </thead>
                <tbody>{sorted.map((r, i) => (
                    <tr key={rowKey ? r[rowKey] + '|' + i : i} onClick={onRow ? () => onRow(r) : undefined} style={{ cursor: onRow ? 'pointer' : 'default' }}>
                        {cols.map(([k, , kind]) => (
                            <td key={k} style={{ textAlign: kind === 's' ? 'left' : 'right', padding: '5px 8px', borderBottom: '1px solid var(--p-color-border-subdued, #eee)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                {cell(r[k], kind, k)}{k === 'store' && markMine && r.mine ? <> <Badge tone="info" size="small">Your store</Badge></> : null}</td>))}
                    </tr>))}</tbody>
            </table>
        </div>
    );
}

function Stat({ label, value, hint }) {
    const body = (
        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
            <BlockStack gap="050"><Text as="p" variant="bodySm" tone="subdued">{label}</Text><Text as="p" variant="headingLg">{value}</Text></BlockStack>
        </Box>);
    return hint ? <Tooltip content={hint}>{body}</Tooltip> : body;
}

export default function ClubMemberAnalyticsReport() {
    const fetchAuth = useAuthFetch();
    // Filters live in the URL (?from=&to=&stores=&clubs=&min=&tab=) so a view can be linked (Eric, 2026-10-16).
    const [sp, setSp] = useSearchParams();
    const TAB_KEYS = ['clubs', 'stores', 'pickup', 'low', 'trend'];
    const [dateFrom, setDateFrom] = useState(sp.get('from') || isoDaysAgo(89));
    const [dateTo, setDateTo] = useState(sp.get('to') || todayIso());
    const [locs, setLocs] = useState(sp.getAll('stores'));           // store slocs ([] = all in scope)
    const [clubs, setClubs] = useState(sp.getAll('clubs'));
    const [minMembers, setMinMembers] = useState(sp.get('min') || '10');
    const [allLocs, setAllLocs] = useState([]);
    const [allClubs, setAllClubs] = useState([]);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [tab, setTabState] = useState(Math.max(0, TAB_KEYS.indexOf(sp.get('tab') || 'clubs')));
    const [drill, setDrill] = useState(null);       // {club, members[]}
    const [help, setHelp] = useState(false);
    // the how-to-read panel moves the table below it: have the fit-to-bottom tables measure again once it has opened or closed
    useEffect(() => {
        const ts = [0, 80, 200, 400].map(ms => setTimeout(() => window.dispatchEvent(new Event('resize')), ms));
        return () => ts.forEach(clearTimeout);
    }, [help]);

    useEffect(() => {
        document.body.classList.add('report-flush-bottom');
        return () => document.body.classList.remove('report-flush-bottom');
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const r = await fetchAuth('/api/reports/club_member_analytics/locations');
                if (r.ok) setAllLocs((await r.json()).locations || []);
            } catch (e) { /* the report call surfaces auth problems */ }
        })();
    }, [fetchAuth]);

    useEffect(() => {
        (async () => {
            try {
                const p = new URLSearchParams(); locs.forEach(l => p.append('locations', l));
                const r = await fetchAuth(`/api/reports/club_member_analytics/clubs?${p}`);
                if (r.ok) setAllClubs((await r.json()).clubs || []);
            } catch (e) { /* ignore */ }
        })();
    }, [fetchAuth, locs]);

    // write the filters (and tab) into the URL, keeping any other params; replace so Back isn't flooded
    const writeUrl = useCallback((f, tabIdx) => {
        setSp(prev => {
            const n = new URLSearchParams(prev);
            ['from', 'to', 'stores', 'clubs', 'min', 'tab'].forEach(k => n.delete(k));
            n.set('from', f.dateFrom); n.set('to', f.dateTo);
            f.locs.forEach(v => n.append('stores', v)); f.clubs.forEach(v => n.append('clubs', v));
            if (String(f.minMembers) !== '10') n.set('min', String(f.minMembers));
            if (tabIdx > 0) n.set('tab', TAB_KEYS[tabIdx]);
            return n;
        }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setSp]);
    const setTab = (i) => { setTabState(i); writeUrl({ dateFrom, dateTo, locs, clubs, minMembers }, i); };

    const run = useCallback(async () => {
        setBusy(true); setErr(null);
        writeUrl({ dateFrom, dateTo, locs, clubs, minMembers }, tab);
        try {
            const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, min_members: String(parseInt(minMembers, 10) || 1) });
            locs.forEach(l => p.append('locations', l)); clubs.forEach(c => p.append('clubs', c));
            const r = await fetchAuth(`/api/reports/club_member_analytics?${p}`);
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
            setData(await r.json());
        } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
    }, [fetchAuth, dateFrom, dateTo, locs, clubs, minMembers, tab, writeUrl]);

    useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

    const openClub = async (row) => {
        try {
            const p = new URLSearchParams({ club: row.club, date_from: dateFrom, date_to: dateTo });
            if (row.sloc !== undefined && row.sloc !== null) p.set('sloc', row.sloc);
            const r = await fetchAuth(`/api/reports/club_member_analytics/members?${p}`);
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
            setDrill({ club: row.club, store: row.store, members: (await r.json()).members || [] });
        } catch (e) { setErr(String(e.message || e)); }
    };

    const locOptions = useMemo(() => allLocs.map(l => ({ value: l.sloc, label: l.name })), [allLocs]);
    const clubOptions = useMemo(() => allClubs.map(c => ({ value: c.club, label: `${c.club} (${fmtN(c.members)})` })), [allClubs]);
    const s = data?.summary;
    const tabs = [
        { id: 'clubs', content: `Clubs${data ? ` (${data.clubs.length})` : ''}` },
        { id: 'stores', content: `Stores${data ? ` (${data.stores.length})` : ''}` },
        { id: 'pickup', content: 'Best pickup-day conversion' },
        { id: 'low', content: 'Lowest engagement' },
        { id: 'trend', content: 'Monthly trend' },
    ];
    const lowRows = useMemo(() => (data?.clubs || []).filter(r => r.members >= 10).sort((a, b) => a.engagement_pct - b.engagement_pct).slice(0, 100), [data]);

    const exportCsv = () => {
        if (!data) return;
        const cols = tab === 1 ? STORE_COLS : CLUB_COLS;
        const rows = tab === 1 ? data.stores : tab === 2 ? data.pickup_conversion : tab === 3 ? lowRows : data.clubs;
        downloadCsv(`club-member-analytics-${dateFrom}-${dateTo}.csv`, [cols.map(c => c[1]), ...rows.map(r => cols.map(c => r[c[0]] ?? ''))]);
    };

    return (
        <BlockStack gap="300">
            {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="200" blockAlign="center" wrap>
                        <ReportDateRange dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
                    </InlineStack>
                    <InlineStack gap="200" blockAlign="center" wrap>
                        <ReportPicker label="Store" options={locOptions} value={locs} onChange={setLocs} multiple searchable placeholder="All my stores" />
                        <ReportPicker label="Club" options={clubOptions} value={clubs} onChange={setClubs} multiple searchable placeholder="All clubs" />
                        <Tooltip content="Hides clubs and stores with fewer members than this (a club's size is the larger of its roster and its pickup customers).">
                            <div style={{ width: 190 }}><TextField label="Min members" labelHidden prefix="Min members" type="number" min={1} value={minMembers} onChange={setMinMembers} autoComplete="off" /></div>
                        </Tooltip>
                        <Button variant="primary" onClick={run} loading={busy}>Run Report</Button>
                        <Button onClick={exportCsv} disabled={!data}>Export CSV</Button>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                        Members come from the Team Portal, counted under their most recent club. Orders are Lightspeed sales matched on email; pickups are OpsHub pickups.
                        {data?.synced_at ? ` Membership synced ${String(data.synced_at).slice(0, 16).replace('T', ' ')}.` : ''}
                    </Text>
                </BlockStack>
            </Card>

            <Card>
                <BlockStack gap="200">
                    <InlineStack align="space-between" blockAlign="center">
                        <Text as="h2" variant="headingMd">How to read this report</Text>
                        <Button variant="plain" onClick={() => setHelp(h => !h)} ariaExpanded={help}>{help ? 'Hide' : 'Show'}</Button>
                    </InlineStack>
                    <Text as="p" variant="bodyMd">
                        This shows how many club families actually shop in our stores. For each club we count the people on its Team Portal roster, then see how many of them bought something in a Soccer Post store, and how much they spent.
                    </Text>
                    <Collapsible open={help} id="cma-help" transition={{ duration: '150ms', timingFunction: 'ease-in-out' }}>
                        <BlockStack gap="200">
                            <List type="bullet">
                                <List.Item><b>Member</b>: a person on a club roster in the Team Portal. If someone is in more than one club, they count only in the one they joined most recently.</List.Item>
                                <List.Item><b>Store</b>: each member is placed at the store of their most recent pickup, or if they have never picked up, their most recent store sale. Members with neither use the club's usual pickup store. So one club can have several rows, for example some players at Manhattan and others at Brooklyn, and the rows add up to the whole club.</List.Item>
                                <List.Item><b>Shopped / Orders / Revenue</b>: sales rung up at any Soccer Post store (the register, not web orders) by a member during the dates you picked. A sale only counts if the customer's email is on the transaction, so members who check out without giving their email are missed.</List.Item>
                                <List.Item><b>% Engaged</b>: Shopped ÷ Members. This is the main health number: a higher percentage means more of the club shops with us.</List.Item>
                                <List.Item><b>Pickups</b>: customers who picked up a web order tagged with this club, at that store. This follows the order's own club tag and pickup store, so it matches the Orders list filtered by club and pickup store. <b>Bought @ Pickup</b> are those who also bought something in a store the same day, and <b>Pickup Conv %</b> is how often that happens. A customer who picks up at two stores counts once at each.</List.Item>
                                <List.Item><b>Dates</b> only change Shopped, Orders, Revenue and Pickups. Members is always today's roster. A club's old-season name (for example 2025-2026) can show its own row, because its orders keep their original tag.</List.Item>
                                <List.Item><b>Stores you see</b> depend on your account: a club shows up if any of its members is at one of your stores. You then see the club's other stores too, marked so you can tell which rows are yours.</List.Item>
                                <List.Item><b>Min members</b> hides clubs and stores smaller than that number. It also hides a club's store rows smaller than that number, though "Club total" still shows the whole club. The page address holds every filter, so you can copy it from the browser to share exactly this view.</List.Item>
                                <List.Item>Click a column title to sort. Click a club to see each member. Hover any column title for its meaning. The roster is refreshed every night around 2:45 AM.</List.Item>
                            </List>
                        </BlockStack>
                    </Collapsible>
                </BlockStack>
            </Card>

            {busy && !data && <InlineStack align="center"><Spinner /></InlineStack>}
            {s && (
                <InlineGrid columns={{ xs: 2, sm: 3, md: 4, lg: 6 }} gap="200">
                    <Stat label="Club members" value={fmtN(s.members)} hint="People in a Team Portal club at the selected stores, each counted once under their most recent club" />
                    <Stat label="Shopped" value={fmtN(s.shopped)} hint="Members with at least one Lightspeed order in the range" />
                    <Stat label="% Engaged" value={fmtP(s.engagement_pct)} hint="Shopped ÷ members" />
                    <Stat label="Revenue" value={fmtUSD(s.revenue)} />
                    <Stat label="Orders" value={fmtN(s.orders)} />
                    <Stat label="Avg order" value={fmtUSD(s.avg_order)} />
                    <Stat label="Rev / member" value={fmtUSD(s.rev_per_member)} />
                    <Stat label="Rev / shopper" value={fmtUSD(s.rev_per_shopper)} />
                    <Stat label="Pickups" value={fmtN(s.pickups)} hint="Customers who picked up an order tagged with these clubs, in the range" />
                    <Stat label="Bought @ pickup" value={fmtN(s.bought_at_pickup)} hint="Pickup customers who also had a store sale the same day" />
                    <Stat label="Pickup conversion" value={fmtP(s.pickup_conversion_pct)} hint="Bought at pickup ÷ pickups" />
                    <Stat label="Clubs" value={fmtN(s.clubs)} />
                </InlineGrid>
            )}

            {data && (
                <Card padding="0">
                    <Tabs tabs={tabs} selected={tab} onSelect={setTab} />
                    <Box padding="300" paddingBlockEnd="0">
                        {tab === 0 && <BlockStack gap="200"><Text as="p" variant="bodySm" tone="subdued">A club appears once per store its members use. Click a row to see the members at that store.</Text><SortTable fit cols={CLUB_COLS} rows={data.clubs} onRow={openClub} rowKey="club" /></BlockStack>}
                        {tab === 1 && <SortTable fit cols={STORE_COLS} rows={data.stores} rowKey="store" />}
                        {tab === 2 && <BlockStack gap="200"><Text as="p" variant="bodySm" tone="subdued">Clubs with at least 5 pickups, best first.</Text><SortTable fit cols={CLUB_COLS} rows={data.pickup_conversion} onRow={openClub} rowKey="club" /></BlockStack>}
                        {tab === 3 && <BlockStack gap="200"><Text as="p" variant="bodySm" tone="subdued">Clubs with at least 10 members, lowest % engaged first.</Text><SortTable fit cols={CLUB_COLS} rows={lowRows} onRow={openClub} rowKey="club" /></BlockStack>}
                        {tab === 4 && <SortTable cols={[['month', 'Month', 's'], ['shoppers', 'Shoppers', 'n'], ['engagement_pct', '% of members', 'p'], ['orders', 'Orders', 'n'], ['revenue', 'Revenue', 'u'], ['pickups', 'Pickups', 'n']]} rows={data.trend} rowKey="month" fit />}
                    </Box>
                </Card>
            )}

            <Modal open={!!drill} onClose={() => setDrill(null)} title={drill ? `${drill.club} — ${drill.store || ''}` : ''} size="large">
                <Modal.Section>
                    {drill && <SortTable cols={MEMBER_COLS} rows={drill.members} rowKey="email" maxHeight={480} />}
                </Modal.Section>
            </Modal>
        </BlockStack>
    );
}
